import { Book, Rocket, Terminal, Settings, Puzzle, Code2, FileText, Users } from 'lucide-react'

export default function Documentation() {
	return (
		<div className="w-full">
			{/* Header */}
			<section className="bg-vscode-editor border-b border-vscode-border py-20">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<h1 className="text-5xl md:text-6xl font-semibold text-vscode-text-bright mb-6 font-sans">
						Documentation
					</h1>
					<p className="text-xl text-vscode-text-muted max-w-3xl mx-auto font-mono">
						Everything you need to get started and master SakaDev
					</p>
				</div>
			</section>

			{/* Quick Links */}
			<section className="py-20 bg-vscode-bg">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
						<QuickLinkCard
							icon={<Rocket className="w-8 h-8" />}
							title="Getting Started"
							description="Install and configure SakaDev"
							href="https://sakadev.site/getting-started/installing-sakadev"
						/>
						<QuickLinkCard
							icon={<Code2 className="w-8 h-8" />}
							title="Features Guide"
							description="Explore all capabilities"
							href="https://sakadev.site/features/at-mentions/overview"
						/>
						<QuickLinkCard
							icon={<Terminal className="w-8 h-8" />}
							title="CLI Reference"
							description="Command-line usage"
							href="https://sakadev.site/sakadev-cli/overview"
						/>
						<QuickLinkCard
							icon={<Puzzle className="w-8 h-8" />}
							title="MCP Integration"
							description="Extend with custom tools"
							href="https://sakadev.site/mcp/mcp-overview"
						/>
					</div>
				</div>
			</section>

			{/* Main Documentation Sections */}
			<section className="py-20 bg-vscode-sidebar border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-semibold text-vscode-text-bright mb-12 font-sans">Documentation Sections</h2>
					<div className="grid gap-8">
						<DocSection
							icon={<Rocket className="w-10 h-10" />}
							title="Getting Started"
							description="New to SakaDev? Start here to install, configure, and run your first task."
							links={[
								{ title: "Installing SakaDev", href: "https://sakadev.site/getting-started/installing-sakadev" },
								{ title: "Selecting Your Model", href: "https://sakadev.site/getting-started/selecting-your-model" },
								{ title: "Your First Project", href: "https://sakadev.site/getting-started/your-first-project" }
							]}
						/>
						<DocSection
							icon={<Book className="w-10 h-10" />}
							title="Best Practices"
							description="Learn how to work effectively with SakaDev and manage context efficiently."
							links={[
								{ title: "Understanding Context Management", href: "https://sakadev.site/prompting/understanding-context-management" },
								{ title: "Prompt Engineering Guide", href: "https://sakadev.site/prompting/prompt-engineering-guide" },
								{ title: "SakaDev Memory Bank", href: "https://sakadev.site/prompting/sakadev-memory-bank" }
							]}
						/>
						<DocSection
							icon={<Terminal className="w-10 h-10" />}
							title="CLI Documentation"
							description="Master the SakaDev command-line interface for automation and CI/CD integration."
							links={[
								{ title: "CLI Overview", href: "https://sakadev.site/sakadev-cli/overview" },
								{ title: "Installation", href: "https://sakadev.site/sakadev-cli/installation" },
								{ title: "Three Core Flows", href: "https://sakadev.site/sakadev-cli/three-core-flows" },
								{ title: "CLI Reference", href: "https://sakadev.site/sakadev-cli/cli-reference" }
							]}
						/>
						<DocSection
							icon={<Code2 className="w-10 h-10" />}
							title="Features"
							description="Comprehensive guides for all SakaDev features and capabilities."
							links={[
								{ title: "@ Mentions Overview", href: "https://sakadev.site/features/at-mentions/overview" },
								{ title: "Commands & Shortcuts", href: "https://sakadev.site/features/commands-and-shortcuts/overview" },
								{ title: "Checkpoints", href: "https://sakadev.site/features/checkpoints" },
								{ title: "SakaDevRules", href: "https://sakadev.site/features/sakadev-rules" },
								{ title: "Slash Commands", href: "https://sakadev.site/features/slash-commands/new-task" },
								{ title: "Task Management", href: "https://sakadev.site/features/tasks/understanding-tasks" }
							]}
						/>
						<DocSection
							icon={<Settings className="w-10 h-10" />}
							title="Model & Provider Configuration"
							description="Configure API providers, select models, and run models locally."
							links={[
								{ title: "Model Selection Guide", href: "https://sakadev.site/core-features/model-selection-guide" },
								{ title: "Anthropic", href: "https://sakadev.site/provider-config/anthropic" },
								{ title: "OpenAI", href: "https://sakadev.site/provider-config/openai" },
								{ title: "OpenRouter", href: "https://sakadev.site/provider-config/openrouter" },
								{ title: "AWS Bedrock", href: "https://sakadev.site/provider-config/aws-bedrock/api-key" },
								{ title: "Running Models Locally", href: "https://sakadev.site/running-models-locally/overview" }
							]}
						/>
						<DocSection
							icon={<Puzzle className="w-10 h-10" />}
							title="MCP Integration"
							description="Extend SakaDev with the Model Context Protocol for custom tools and integrations."
							links={[
								{ title: "MCP Overview", href: "https://sakadev.site/mcp/mcp-overview" },
								{ title: "Adding MCP Servers", href: "https://sakadev.site/mcp/adding-mcp-servers-from-github" },
								{ title: "Configuring MCP Servers", href: "https://sakadev.site/mcp/configuring-mcp-servers" },
								{ title: "MCP Marketplace", href: "https://sakadev.site/mcp/mcp-marketplace" },
								{ title: "Server Development", href: "https://sakadev.site/mcp/mcp-server-development-protocol" }
							]}
						/>
						<DocSection
							icon={<FileText className="w-10 h-10" />}
							title="Tools Reference"
							description="Detailed documentation on SakaDev's built-in tools and capabilities."
							links={[
								{ title: "Tools Guide", href: "https://sakadev.site/exploring-sakadevs-tools/sakadev-tools-guide" },
								{ title: "New Task Tool", href: "https://sakadev.site/exploring-sakadevs-tools/new-task-tool" },
								{ title: "Remote Browser Support", href: "https://sakadev.site/exploring-sakadevs-tools/remote-browser-support" }
							]}
						/>
						<DocSection
							icon={<Users className="w-10 h-10" />}
							title="Enterprise & Support"
							description="Enterprise solutions, security information, and troubleshooting guides."
							links={[
								{ title: "Enterprise Overview", href: "https://sakadev.site/enterprise-solutions/overview" },
								{ title: "Security Concerns", href: "https://sakadev.site/enterprise-solutions/security-concerns" },
								{ title: "Terminal Quick Fixes", href: "https://sakadev.site/troubleshooting/terminal-quick-fixes" },
								{ title: "Terminal Integration Guide", href: "https://sakadev.site/troubleshooting/terminal-integration-guide" }
							]}
						/>
					</div>
				</div>
			</section>

			{/* Community Resources */}
			<section className="py-20 bg-vscode-bg border-t border-vscode-border">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<h2 className="text-3xl font-semibold text-vscode-text-bright mb-12 text-center font-sans">Community Resources</h2>
					<div className="grid md:grid-cols-3 gap-8">
						<CommunityCard
							title="GitHub"
							description="View source code, report issues, and contribute to the project."
							href="https://github.com/rahmanazhar/SakaDev"
							cta="View Repository"
						/>
						<CommunityCard
							title="Discord"
							description="Join our community for support, discussions, and updates."
							href="https://discord.gg/sakadev"
							cta="Join Discord"
						/>
						<CommunityCard
							title="Reddit"
							description="Discuss SakaDev with the community on r/sakadev."
							href="https://www.reddit.com/r/sakadev/"
							cta="Visit Subreddit"
						/>
					</div>
				</div>
			</section>
		</div>
	)
}

function QuickLinkCard({ 
	icon, 
	title, 
	description, 
	href 
}: { 
	icon: React.ReactNode
	title: string
	description: string
	href: string
}) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="p-6 bg-vscode-editor rounded-lg transition-all border border-vscode-border hover:border-vscode-accent group"
		>
			<div className="text-vscode-accent mb-4 group-hover:scale-110 transition-transform">{icon}</div>
			<h3 className="text-lg font-semibold text-vscode-text-bright mb-2 font-sans">{title}</h3>
			<p className="text-sm text-vscode-text-muted font-mono">{description}</p>
		</a>
	)
}

function DocSection({ 
	icon, 
	title, 
	description, 
	links 
}: { 
	icon: React.ReactNode
	title: string
	description: string
	links: { title: string; href: string }[]
}) {
	return (
		<div className="p-8 bg-vscode-editor rounded-lg border border-vscode-border">
			<div className="flex items-start space-x-4 mb-6">
				<div className="text-vscode-accent">{icon}</div>
				<div>
					<h3 className="text-2xl font-semibold text-vscode-text-bright mb-2 font-sans">{title}</h3>
					<p className="text-vscode-text-muted font-mono">{description}</p>
				</div>
			</div>
			<ul className="space-y-3 ml-14">
				{links.map((link) => (
					<li key={link.href}>
						<a
							href={link.href}
							target="_blank"
							rel="noopener noreferrer"
							className="text-vscode-accent hover:text-vscode-text-bright transition-colors hover:underline font-mono"
						>
							{link.title} →
						</a>
					</li>
				))}
			</ul>
		</div>
	)
}

function CommunityCard({ 
	title, 
	description, 
	href, 
	cta 
}: { 
	title: string
	description: string
	href: string
	cta: string
}) {
	return (
		<div className="p-8 bg-vscode-sidebar rounded-lg border border-vscode-border">
			<h3 className="text-xl font-semibold text-vscode-text-bright mb-3 font-sans">{title}</h3>
			<p className="text-vscode-text-muted mb-6 font-mono">{description}</p>
			<a
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="inline-block px-6 py-3 bg-vscode-accent hover:bg-vscode-text-bright hover:text-vscode-bg text-white rounded-lg transition-colors font-medium font-sans"
			>
				{cta}
			</a>
		</div>
	)
}
